import React, { useState } from 'react';
import { Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronUp,
  faChevronDown,
  faCheck,
  faReply,
  faTimes,
  faPaperPlane,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Comments panel for DBQ forms.
 * Displays comments for the active field with navigation, replies, and resolution.
 */
const DbqCommentsPanel = ({
  comments = [],
  fieldId,
  fieldLabel,
  canComment = false,
  canResolve = false,
  canResolveComment,
  currentUser,
  onResolve,
  onReply,
  onAddComment,
  onNavigate,
  commentFieldIds = [],
  onClose,
  embedded = false,
}) => {
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [newCommentText, setNewCommentText] = useState('');
  const [showNewComment, setShowNewComment] = useState(false);

  // Get current field's position in the comment field list
  const currentIndex = commentFieldIds.indexOf(fieldId);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < commentFieldIds.length - 1 && currentIndex !== -1;

  // Format date for display
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  // Handle navigation
  const handleNavigate = (direction) => {
    if (direction === 'up' && hasPrev) {
      onNavigate(commentFieldIds[currentIndex - 1]);
    } else if (direction === 'down' && hasNext) {
      onNavigate(commentFieldIds[currentIndex + 1]);
    }
  };

  // Handle reply submission
  const handleReplySubmit = (commentId) => {
    if (replyText.trim() && onReply) {
      onReply(fieldId, commentId, currentUser, replyText.trim());
      setReplyText('');
      setReplyingTo(null);
    }
  };

  // Handle new comment submission
  const handleNewCommentSubmit = () => {
    if (newCommentText.trim() && onAddComment) {
      onAddComment(fieldId, currentUser, newCommentText.trim());
      setNewCommentText('');
      setShowNewComment(false);
    }
  };

  const content = (
    <>
      <div className="dbq-comments-list">
        {comments.length === 0 ? (
          <div className="dbq-no-comments">
            <p>No comments yet</p>
            {canComment && !showNewComment && (
              <Button
                variant="outline-primary"
                size="sm"
                onClick={() => setShowNewComment(true)}
              >
                Add first comment
              </Button>
            )}
          </div>
        ) : (
          <>
            {comments.map((comment) => (
              <div key={comment.id} className="dbq-comment-card">
                {/* Comment Header */}
                <div className="dbq-comment-header">
                  <span className="dbq-comment-author">{comment.author?.name || 'Unknown'}</span>
                  <span className="dbq-comment-date">{formatDate(comment.createdAt)}</span>
                </div>

                {/* Comment Body */}
                <div className="dbq-comment-body">
                  {comment.text}
                </div>

                {/* Comment Actions */}
                <div className="dbq-comment-actions">
                  {canComment && onReply ? (
                    <Button
                      variant="link"
                      size="sm"
                      className="dbq-action-btn"
                      onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)}
                    >
                      <FontAwesomeIcon icon={faReply} className="me-1" />
                      Reply
                    </Button>
                  ) : null}
                  {((typeof canResolveComment === 'function' && canResolveComment(comment)) || canResolve) && onResolve && (
                    <Button
                      variant="link"
                      size="sm"
                      className="dbq-action-btn dbq-resolve-btn"
                      onClick={() => onResolve(fieldId, comment.id)}
                    >
                      <FontAwesomeIcon icon={faCheck} className="me-1" />
                      Resolve
                    </Button>
                  )}
                </div>

                {/* Replies */}
                {comment.replies && comment.replies.length > 0 && (
                  <div className="dbq-replies">
                    {comment.replies.map((reply) => (
                      <div key={reply.id} className="dbq-reply">
                        <div className="dbq-reply-header">
                          <span className="dbq-reply-author">{reply.author?.name || 'Unknown'}</span>
                          <span className="dbq-reply-date">{formatDate(reply.createdAt)}</span>
                        </div>
                        <div className="dbq-reply-body">{reply.text}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Input */}
                {canComment && onReply && replyingTo === comment.id && (
                  <div className="dbq-reply-input animate-fade-in">
                    <Form.Control
                      as="textarea"
                      rows={2}
                      value={replyText}
                      onChange={(e) => setReplyText(e.target.value)}
                      placeholder="Write a reply..."
                      className="dbq-reply-textarea"
                      autoFocus
                    />
                    <div className="dbq-reply-actions">
                      <Button
                        variant="link"
                        size="sm"
                        onClick={() => {
                          setReplyingTo(null);
                          setReplyText('');
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleReplySubmit(comment.id)}
                        disabled={!replyText.trim()}
                      >
                        <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                        Send
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {canComment && comments.length > 0 && !showNewComment && (
        <div className="dbq-add-comment-trigger">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => setShowNewComment(true)}
          >
            Add comment
          </Button>
        </div>
      )}

      {canComment && showNewComment && (
        <div className="dbq-new-comment animate-fade-in">
          <Form.Control
            as="textarea"
            rows={3}
            value={newCommentText}
            onChange={(e) => setNewCommentText(e.target.value)}
            placeholder="Write a comment..."
            className="dbq-new-comment-textarea"
            autoFocus
          />
          <div className="dbq-new-comment-actions">
            <Button
              variant="link"
              size="sm"
              onClick={() => {
                setShowNewComment(false);
                setNewCommentText('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleNewCommentSubmit}
              disabled={!newCommentText.trim()}
            >
              <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
              Post
            </Button>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) {
    return content;
  }

  return (
    <div className="dbq-comments-panel">
      <div className="dbq-comments-header">
        <div className="dbq-comments-title">
          <h4>Comments</h4>
          <span className="dbq-comments-field-label">{fieldLabel}</span>
        </div>
        <div className="dbq-comments-nav">
          <Button
            variant="link"
            className="dbq-nav-btn"
            onClick={() => handleNavigate('up')}
            disabled={!hasPrev}
            title="Previous comment"
          >
            <FontAwesomeIcon icon={faChevronUp} />
          </Button>
          <Button
            variant="link"
            className="dbq-nav-btn"
            onClick={() => handleNavigate('down')}
            disabled={!hasNext}
            title="Next comment"
          >
            <FontAwesomeIcon icon={faChevronDown} />
          </Button>
          <Button
            variant="link"
            className="dbq-close-btn"
            onClick={onClose}
            title="Close comments"
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </div>
      </div>
      {content}
    </div>
  );
};

export default DbqCommentsPanel;
